import { ButtonIcon } from '@/components';
import { cn } from '@/lib';
import { formatDate } from '@/utils/dateUtils';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { RepairHistoryEntry } from '../types';
import { openRepairDocument } from './openRepairDocument';

interface RepairHistoryCardRowProps {
  entry: RepairHistoryEntry;
  // When set, the whole card opens the edit side panel (write permission).
  onEdit?: () => void;
}

// RepairHistoryCardRow renders one repair entry as a card: the coloured state above the name
// with an inline Open icon on the left, and the date on the right. It is a div (not a button)
// so the Open icon button can nest without invalid interactive-element nesting.
export function RepairHistoryCardRow({ entry, onEdit }: RepairHistoryCardRowProps) {
  const canEdit = Boolean(onEdit);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onEdit) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onEdit();
    }
  };

  return (
    <div
      role={canEdit ? 'button' : undefined}
      tabIndex={canEdit ? 0 : undefined}
      onClick={onEdit}
      onKeyDown={handleKeyDown}
      className={cn(
        'border-border flex w-full justify-between gap-3 rounded-lg border px-3 py-3 text-left items-center',
        canEdit &&
        'hover:border-primary/50 hover:bg-surface-darker focus:ring-primary cursor-pointer transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none'
      )}
    >
      <div className="flex flex-col">
        <div className="flex items-center gap-2 w-full">
          <span className="text-onsurface text-xs">{formatDate(entry.date)}</span>
          {/*   <span className="bg-onsurface/80 h-1 w-1 rounded-full" aria-hidden="true" />
 */}
        </div>

        <div className="flex items-center gap-2 justify-between w-full">
          <span className="text-onsurface font-medium">{entry.name}</span>
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">

        <span className="text-onsurface/80 text-sm">
          <RepairStateText state={entry.state} />
        </span>
        {entry.documentAvailable && <OpenDocumentButton repairId={entry.id} />}
      </div>
    </div>
  );
}

// RepairStateText translates the repair state key for the coloured label.
function RepairStateText({ state }: { state: RepairHistoryEntry['state'] }) {
  const { t } = useTranslation();
  return <>{t(`repairHistory.state.${state}`)}</>;
}

// OpenDocumentButton opens the entry's document without triggering the surrounding edit card.
function OpenDocumentButton({ repairId }: { repairId: string }) {
  const { t } = useTranslation();

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    void openRepairDocument(repairId, () =>
      toast.error(t('repairHistory.toast.openError'))
    );
  };

  return (
    <ButtonIcon
      icon="download"
      ariaLabel={t('repairHistory.actions.open')}
      onClick={handleOpen}
    />
  );
}
